import { useState, useEffect } from 'react'
import { X, Trash2, Plus, Loader2, Check, Wand2, RefreshCw } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'

// Step 20: AliasesEditor gains a Pending review tab for Claude-proposed
// clusters and a Run Claude alias scan button. The legacy Active tab
// behavior is unchanged.

export default function AliasesEditor({ onClose }) {
  const api = useHighlightsApi()
  const [tab, setTab] = useState('active')  // 'active' | 'proposed' | 'rejected'
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [alias, setAlias] = useState('')
  const [canonical, setCanonical] = useState('')
  const [saving, setSaving] = useState(false)
  const [q, setQ] = useState('')
  const [pendingCount, setPendingCount] = useState(null)
  const [scanState, setScanState] = useState(null)  // { queued } | { error } | null

  async function load() {
    setLoading(true)
    setErr(null)
    try {
      const r = await api.aliases({ q: q || undefined, status: tab })
      setRows(r.aliases || [])
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [tab])

  // Lightweight count loader for the Pending review tab badge — runs on
  // mount + whenever the user navigates back to the modal (catches scan
  // results landing in the background).
  async function refreshPendingCount() {
    try {
      const r = await api.aliases({ status: 'proposed' })
      setPendingCount((r.aliases || []).length)
    } catch { /* ignore */ }
  }
  useEffect(() => { refreshPendingCount() }, [])

  async function runScan({ retryErrored = false } = {}) {
    const verb = retryErrored ? 'Retry failed' : 'Run'
    const target = retryErrored
      ? 'all videos whose previous Claude scan errored'
      : 'all indexed captioned videos without a completed scan'
    if (!confirm(`${verb} Claude alias scan across ${target}?\n\nEstimated cost: ~$0.016 per video. Runs serially in the background to stay under Anthropic's rate limit (~6–10 videos/min). Refresh the Pending review tab in a few minutes.`)) return
    setScanState({ pending: true })
    try {
      const r = await api.scanAliases({ retryErrored })
      setScanState({ queued: r.queued, mode: r.mode })
      setTimeout(() => { refreshPendingCount(); load() }, 2000)
    } catch (e) {
      setScanState({ error: e.message })
    }
  }

  async function reviewRow(aliasId, { canonical, status }) {
    try {
      await api.reviewAlias(aliasId, { canonical, status })
      // After a review, refresh both the visible list AND the pending badge.
      await Promise.all([load(), refreshPendingCount()])
    } catch (e) {
      alert(`Review failed: ${e.message}`)
    }
  }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function add(e) {
    e.preventDefault()
    if (!alias.trim() || !canonical.trim()) return
    setSaving(true)
    try {
      await api.createAlias({ alias: alias.trim(), canonical: canonical.trim() })
      setAlias('')
      setCanonical('')
      await load()
    } catch (e) {
      alert(`Add failed: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function remove(a) {
    if (!confirm(`Delete alias "${a}"?`)) return
    try {
      await api.deleteAlias(a)
      await load()
    } catch (e) {
      alert(`Delete failed: ${e.message}`)
    }
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
    >
      <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 8, width: 'min(700px, 96vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Search aliases</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              Map common STT mishearings to the correct spelling. The search expander ORs all related terms.
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Tab strip */}
        <div style={{ padding: '0 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', gap: 4 }}>
          {[
            { k: 'active',   label: 'Active' },
            { k: 'proposed', label: 'Pending review', badge: pendingCount },
            { k: 'rejected', label: 'Rejected' },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              style={{
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: '2px solid ' + (tab === t.k ? '#3b82f6' : 'transparent'),
                color: tab === t.k ? '#bfdbfe' : '#888',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: tab === t.k ? 600 : 500,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              {t.label}
              {typeof t.badge === 'number' && t.badge > 0 && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1e3a8a', color: '#bfdbfe', fontWeight: 600 }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button onClick={() => runScan()} disabled={scanState?.pending} title="Sweep all indexed videos with Claude to propose alias clusters"
            style={{ alignSelf: 'center', padding: '4px 10px', background: '#1e1b4b', border: '1px solid #4338ca', borderRadius: 4, color: '#c4b5fd', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {scanState?.pending ? <Loader2 size={12} className="spin" /> : <Wand2 size={12} />}
            Run Claude scan
          </button>
          <button onClick={() => runScan({ retryErrored: true })} disabled={scanState?.pending} title="Re-run only the videos whose previous Claude scan errored"
            style={{ alignSelf: 'center', marginLeft: 4, padding: '4px 10px', background: 'transparent', border: '1px solid #4338ca', borderRadius: 4, color: '#c4b5fd', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={12} />
            Retry failed
          </button>
          <button onClick={() => { refreshPendingCount(); load() }} title="Refresh" style={{ alignSelf: 'center', marginLeft: 6, padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#888', cursor: 'pointer' }}>
            <RefreshCw size={12} />
          </button>
        </div>
        {scanState && !scanState.pending && (
          <div style={{ padding: '8px 16px', fontSize: 12, color: scanState.error ? 'crimson' : '#86efac', background: scanState.error ? 'rgba(220,38,38,0.08)' : 'rgba(34,197,94,0.08)', borderBottom: '1px solid #1f1f1f' }}>
            {scanState.error
              ? `Scan failed: ${scanState.error}`
              : `Queued ${scanState.queued} videos for Claude alias scan — results land in Pending review as they complete.`}
          </div>
        )}

        <form onSubmit={add} style={{ padding: '12px 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="alias (what STT heard, e.g. kazrowski)"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            style={{ flex: 1, minWidth: 180, padding: '8px 10px', background: '#000', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
          />
          <span style={{ color: '#666' }}>→</span>
          <input
            type="text"
            placeholder="canonical (correct spelling, e.g. kaczorowski)"
            value={canonical}
            onChange={(e) => setCanonical(e.target.value)}
            style={{ flex: 1, minWidth: 180, padding: '8px 10px', background: '#000', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
          />
          <button type="submit" disabled={saving || !alias.trim() || !canonical.trim()}
            style={{ padding: '8px 14px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {saving ? <Loader2 size={12} className="spin" /> : <Plus size={12} />} Add
          </button>
        </form>

        <div style={{ padding: '8px 16px', borderBottom: '1px solid #1f1f1f' }}>
          <input
            type="text"
            placeholder="Filter…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load() }}
            onBlur={load}
            style={{ width: '100%', padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', fontSize: 13 }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 16, color: '#888', textAlign: 'center' }}>Loading…</div>}
          {err && <div style={{ padding: 16, color: 'crimson' }}>{err}</div>}
          {!loading && rows.length === 0 && (
            <div style={{ padding: 24, color: '#888', textAlign: 'center', fontSize: 13 }}>
              No aliases yet. Add one above.
            </div>
          )}
          {rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#161616', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Alias</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Canonical</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', width: 90 }}>Source</th>
                  {tab === 'proposed' && <th style={{ padding: '8px 12px', textAlign: 'right', width: 60 }}>Conf.</th>}
                  <th style={{ padding: '8px 12px', textAlign: 'right', width: 180 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.alias} style={{ borderBottom: '1px solid #1a1a1a' }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{r.alias}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{r.canonical}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <SourceBadge proposedBy={r.proposed_by} />
                    </td>
                    {tab === 'proposed' && (
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', color: r.confidence >= 0.85 ? '#86efac' : r.confidence >= 0.6 ? '#facc15' : '#fb923c' }}>
                        {r.confidence != null ? r.confidence.toFixed(2) : '—'}
                      </td>
                    )}
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {tab === 'proposed' && (
                        <>
                          <button onClick={() => reviewRow(r.alias, { status: 'active' })} title="Approve — flip to active"
                            style={{ background: 'transparent', border: '1px solid #166534', borderRadius: 4, color: '#86efac', cursor: 'pointer', padding: '2px 8px', fontSize: 11, marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Check size={11} /> Approve
                          </button>
                          <button onClick={() => {
                            const next = prompt(`Edit canonical for "${r.alias}":`, r.canonical)
                            if (next != null && next.trim() && next !== r.canonical) {
                              reviewRow(r.alias, { canonical: next.trim(), status: 'active' })
                            }
                          }} title="Edit canonical + approve"
                            style={{ background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#bbb', cursor: 'pointer', padding: '2px 8px', fontSize: 11, marginRight: 4 }}>
                            Edit
                          </button>
                          <button onClick={() => reviewRow(r.alias, { status: 'rejected' })} title="Reject — keep out of search"
                            style={{ background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#dc2626', cursor: 'pointer', padding: '2px 8px', fontSize: 11 }}>
                            Reject
                          </button>
                        </>
                      )}
                      {tab === 'rejected' && (
                        <button onClick={() => reviewRow(r.alias, { status: 'active' })} title="Reinstate"
                          style={{ background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#bbb', cursor: 'pointer', padding: '2px 8px', fontSize: 11 }}>
                          Reinstate
                        </button>
                      )}
                      {tab === 'active' && (
                        <button onClick={() => remove(r.alias)} title="Delete"
                          style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4 }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
      </div>
    </div>
  )
}

function SourceBadge({ proposedBy }) {
  if (proposedBy === 'claude_haiku' || proposedBy === 'claude_sonnet') {
    return (
      <span title="Proposed by Claude alias scanner"
        style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1e1b4b', color: '#c4b5fd', fontWeight: 600 }}>
        🤖 claude
      </span>
    )
  }
  if (proposedBy === 'editor' || !proposedBy) {
    return (
      <span title="Hand-curated by an editor"
        style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#bbb', fontWeight: 600 }}>
        👤 editor
      </span>
    )
  }
  return (
    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#888', fontWeight: 600 }}>
      {proposedBy}
    </span>
  )
}
